import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Home, BookOpen, Map, Users, Sparkles, Compass, Flame, KeyRound, Menu, X, Trophy, LogOut, Image as ImageIcon } from "lucide-react";
import { Toaster } from "sonner";
import StickyJoin from "./StickyJoin";
import AnimeNYCSplash from "./AnimeNYCSplash";
import AudioToggle from "./AudioToggle";
import ArchiveTriviaModal from "./ArchiveTriviaModal";
import { useAuth, buildEmergentLoginUrl } from "../../lib/auth";

// Primary nav (top header + mobile hamburger)
const NAV = [
  { to: "/", label: "Home", icon: Home, testId: "nav-home" },
  { to: "/pilot", label: "Pilot", icon: BookOpen, testId: "nav-pilot" },
  { to: "/nations", label: "Nations", icon: Map, testId: "nav-nations" },
  { to: "/characters", label: "Cast", icon: Users, testId: "nav-characters" },
  { to: "/gallery", label: "Gallery", icon: ImageIcon, testId: "nav-gallery" },
  { to: "/nation-quiz", label: "Quiz", icon: Compass, testId: "nav-quiz" },
  { to: "/archive", label: "Archive", icon: KeyRound, testId: "nav-archive" },
  { to: "/mission", label: "Mission", icon: Flame, testId: "nav-mission" },
  { to: "/community", label: "Join", icon: Sparkles, testId: "nav-community" },
];

// Bottom nav (mobile only) — kept to 6 items for tap-target comfort
const BOTTOM_NAV = NAV.filter((n) => n.to !== "/mission" && n.to !== "/community");

const MobileAuthLinks = () => {
  const { user, signOut } = useAuth();
  if (!user) {
    return (
      <button
        type="button"
        onClick={() => { window.location.href = buildEmergentLoginUrl("/auth/callback"); }}
        data-testid="mobile-signin"
        className="mt-3 w-full text-left py-3 px-2 text-sm tracking-wide bg-[#C5A059] text-black uppercase"
      >
        Enter The Archive
      </button>
    );
  }
  return (
    <>
      <Link
        to="/me/cast"
        data-testid="mobile-mycast"
        className="py-3 px-2 text-sm tracking-wide text-[#C5A059] border-b border-white/5"
      >
        My Cast
      </Link>
      <button
        type="button"
        onClick={signOut}
        data-testid="mobile-signout"
        className="py-3 px-2 text-sm tracking-wide text-[#A39E99] text-left"
      >
        Sign out
      </button>
    </>
  );
};

const AuthButton = () => {
  const { user, signOut } = useAuth();
  if (!user) {
    return (
      <button
        type="button"
        onClick={() => { window.location.href = buildEmergentLoginUrl("/auth/callback"); }}
        data-testid="header-signin"
        className="ml-2 text-xs tracking-[0.18em] uppercase bg-[#C5A059] text-black px-4 py-2 hover:bg-[#D4AF68] transition-colors"
      >
        Enter The Archive
      </button>
    );
  }
  return (
    <div className="ml-2 flex items-center gap-3" data-testid="header-user">
      {user.is_owner && (
        <span
          className="text-[9px] tracking-[0.28em] uppercase border border-[#9F7FE0]/60 text-[#9F7FE0] px-2 py-1 bg-[#9F7FE0]/10"
          data-testid="header-owner-badge"
          title="Shadow Mode — your guesses do not affect global state or leaderboard"
        >
          Owner · Shadow
        </span>
      )}
      <Link
        to="/me/cast"
        data-testid="header-mycast"
        className="text-xs tracking-[0.18em] uppercase border border-[#C5A059]/40 text-[#C5A059] px-3 py-2 hover:bg-[#C5A059]/10"
      >
        My Cast
      </Link>
      <button
        type="button"
        onClick={signOut}
        data-testid="header-signout"
        title="Sign out"
        className="text-[#A39E99] hover:text-[#EAE0D5] p-1"
      >
        <LogOut size={16} />
      </button>
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/5"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 md:h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="brand-link"
        >
          <span className="inline-block w-2 h-2 rotate-45 bg-[#C5A059]" />
          <span className="font-display text-base md:text-lg tracking-wide text-[#EAE0D5]">
            Chains Beneath{" "}
            <span className="text-[#C5A059]">Bakhtak</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map(({ to, label, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-[#C5A059]"
                    : "text-[#EAE0D5]/80 hover:text-[#EAE0D5]"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
          <Link
            to="/leaderboard"
            data-testid="nav-leaderboard"
            className="text-sm tracking-wide text-[#EAE0D5]/80 hover:text-[#EAE0D5] flex items-center gap-1.5"
          >
            <Trophy size={14} /> Leaderboard
          </Link>
          <AudioToggle />
          <AuthButton />
        </nav>

        {/* Mobile menu button + audio toggle */}
        <div className="md:hidden flex items-center gap-2">
          <AudioToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="p-2 -mr-2 text-[#EAE0D5]"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown panel */}
      {open && (
        <div
          className="md:hidden border-t border-white/5 bg-[#050505]/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map(({ to, label, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`mobile-${testId}`}
                className={({ isActive }) =>
                  `py-3 px-2 text-sm tracking-wide border-b border-white/5 ${
                    isActive ? "text-[#C5A059]" : "text-[#EAE0D5]/85"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
            <NavLink
              to="/leaderboard"
              data-testid="mobile-nav-leaderboard"
              className={({ isActive }) =>
                `py-3 px-2 text-sm tracking-wide border-b border-white/5 ${
                  isActive ? "text-[#C5A059]" : "text-[#EAE0D5]/85"
                }`
              }
            >
              Leaderboard
            </NavLink>
            <MobileAuthLinks />
          </div>
        </div>
      )}
    </header>
  );
};

const BottomNav = () => (
  <nav
    className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 pb-safe"
    data-testid="bottom-nav"
  >
    <ul className="grid grid-cols-6">
      {BOTTOM_NAV.map(({ to, label, icon: Icon, testId }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={to === "/"}
            data-testid={`bottom-${testId}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-wider uppercase transition-colors ${
                isActive
                  ? "text-[#C5A059]"
                  : "text-[#EAE0D5]/70 hover:text-[#EAE0D5]"
              }`
            }
          >
            <Icon size={18} strokeWidth={1.5} />
            <span>{label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  </nav>
);

const Footer = () => (
  <footer
    className="border-t border-white/5 mt-24 md:mt-32"
    data-testid="site-footer"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid gap-10 md:grid-cols-3">
      <div>
        <div className="font-display text-xl text-[#EAE0D5]">
          Chains Beneath <span className="text-[#C5A059]">Bakhtak</span>
        </div>
        <p className="mt-3 text-sm text-[#A39E99] max-w-sm leading-relaxed">
          An original dark-fantasy mythos. Nine nations. Nine bloodlines. One
          demon that rides the chest.
        </p>
      </div>
      <div className="text-sm text-[#A39E99]">
        <div className="text-[#EAE0D5] mb-3 tracking-widest text-xs uppercase">
          Explore
        </div>
        <ul className="space-y-2">
          <li><Link to="/mission" className="hover:text-[#C5A059]" data-testid="footer-mission">Mission</Link></li>
          <li><Link to="/pilot" className="hover:text-[#C5A059]" data-testid="footer-pilot">Read the Pilot</Link></li>
          <li><Link to="/nations" className="hover:text-[#C5A059]" data-testid="footer-nations">Nations</Link></li>
          <li><Link to="/characters" className="hover:text-[#C5A059]" data-testid="footer-characters">Characters</Link></li>
          <li><Link to="/nation-quiz" className="hover:text-[#C5A059]" data-testid="footer-quiz">Nation Quiz</Link></li>
          <li><Link to="/archive" className="hover:text-[#C5A059]" data-testid="footer-archive">The Archive</Link></li>
          <li><Link to="/community" className="hover:text-[#C5A059]" data-testid="footer-community">Community</Link></li>
        </ul>
      </div>
      <div className="text-sm text-[#A39E99]">
        <div className="text-[#EAE0D5] mb-3 tracking-widest text-xs uppercase">
          The Scroll
        </div>
        <p className="leading-relaxed">
          © {new Date().getFullYear()} Chains Beneath Bakhtak · Created by Isaiah M. Willis-Dean.
        </p>
        <p className="mt-3 text-xs text-[#737373]">
          Original IP in active development. All artwork from the world bible.
        </p>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen grain bg-[#050505] text-[#EAE0D5]">
      <Header />
      <main
        className="pt-14 md:pt-16 pb-24 md:pb-0 page-fade-in"
        data-testid="page-main"
      >
        {children}
      </main>
      <Footer />
      <BottomNav />
      <StickyJoin />
      <AnimeNYCSplash />
      <ArchiveTriviaModal />
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(197,160,89,0.25)",
            color: "#EAE0D5",
            fontFamily: "Outfit, sans-serif",
          },
        }}
      />
    </div>
  );
}
